import { Injectable, EventEmitter } from '@angular/core';
import { ApiService } from '../../core/services/api.service';
import { Shop } from '../../shared/entities/shop';
import { Observable, forkJoin, Subscription, Subject, Observer } from 'rxjs';
import { MulticastObserverFactoryService } from '../../core/services/multicast-observer-factory.service';
import { ShopListInterface } from '../../shared/interfaces/shop-list-interface';
import { OfferInterface } from '../../shared/interfaces/offer-interface';
import { BookingParameters, BookingParametersInterface } from '../../shared/entities/bookingParameters';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { BookingParameterService } from '../../core/services/booking-parameter.service';

export enum eventTimeType {
  preInTown, // before in town offers are loaded
  onInTown, // when in town offers have been loaded
  // preOutOfTown, // before out of town offers are loaded <- not in use, if you need it, add it!
  onOutOfTown, // when out of town offers have been loaded
  onAllTown, // when in AND out of town offers have been loaded
  preShopUpdate,
  onShopUpdate
}

@Injectable({
  providedIn: 'root'
})
export class ShopService {
  private isBrowser = this.platformService.getIsBrowser();
  public offers$: Observable<Array<OfferInterface>>; // subscribe to get offer infos
  public outOfTownOffers$: Observable<Array<OfferInterface>>; // subscribe to get offer infos

  private _shops: Array<Shop> = [];
  private _shopApiCache: Array<Shop>; // cache for shops api response
  private offers: Array<OfferInterface>; // cache for offers
  private outOfTownOffers: Array<OfferInterface>; // cache for offers

  private offerSub: Subscription;
  private platformCustomerCountrySub: Subscription;
  private customerCountryCode: string;

  private _lastSearchedSlug: string;

  private inTownOffer$ = new Subject();
  private outOfTownOffer$ = new Subject();

  // multicast shop observable. Set to null to refresh when data has changed
  private _shops$: Observable<ShopListInterface[]>;
  get shops$() {
    return this._shops$
      ? this._shops$
      : this.multicastObservableFactory.create<ShopListInterface[]>(this.loadShopData);
  }
  set shops$(val) {
    this._shops$ = val;
  }

  // TODO: write interfaces for that since .pipe(filter()) -> can not type infer correctly.
  // something like filter(event is preShopUpdateEventInterface => event.type === eventTimeType.preShopUpdate)
  // would be great typing here
  readonly updates: EventEmitter<
    | { type: eventTimeType.preShopUpdate; data: { bookingParameters: BookingParameters } }
    | { type: eventTimeType.onShopUpdate; data: { bookingParameters: BookingParameters; shops: Array<Shop> } }
    | { type: eventTimeType.preInTown; data: { bookingParameters: BookingParameters } }
    | { type: eventTimeType.onInTown; data: { bookingParameters: BookingParameters; shops: Array<Shop> } }
    // | { type: eventTimeType.preOutOfTown; data: { bookingParameters: BookingParameters } }
    | { type: eventTimeType.onOutOfTown; data: { bookingParameters: BookingParameters; shops: Array<Shop> } }
    | { type: eventTimeType.onAllTown; data: { bookingParameters: BookingParameters; shops: Array<Shop> } }
  > = new EventEmitter(true);

  readonly loadingError: EventEmitter<{ bookingParameters: BookingParameters; error: string }> =
    new EventEmitter<{ bookingParameters: BookingParameters; error: string }>();

  public get currentShops(): Shop[] {
    return this._shops;
  }

  private get inTownShopIds() {
    if (!this._shops) {
      return [];
    }
    let shops = this._shops.filter(
      shop =>
        shop.inTown === true &&
        shop.unbookableReasonsFor(
          this.bookingParameters.start,
          this.bookingParameters.end,
          this.bookingParameters.ages
        ).length === 0
    );
    if (shops.length === 0) {
      shops = this._shops.filter(shop => shop.distance <= 1.5);
    }
    return shops.map(shop => shop.id);
  }

  private get outOfTownShopIds() {
    if (!this._shops) {
      return [];
    }
    // if no shops are in town, distance is used. get shops that are beyond this distance
    let shops = this._shops.filter(shop => shop.inTown);
    if (shops.length === 0) {
      shops = this._shops.filter(shop => shop.distance > 1.5);
    } else {
      // in town shops exist, load out of town shops
      shops = this._shops.filter(shop => !shop.inTown);
    }
    return shops.map(shop => shop.id);
  }

  get bookingParameters() {
    return this.bookingParameterService.bookingParameters;
  }

  set bookingParameters(bookingParameters) {
    this.bookingParameters = bookingParameters;
  }

  constructor(
    private apiService: ApiService,
    private multicastObservableFactory: MulticastObserverFactoryService,
    private platformService: PlatformInformationService,
    private bookingParameterService: BookingParameterService
  ) {}

  /**
   * updates Shop Data and leads to reloads of shops, offers or both.
   * will lead to lots of eventemitter firing!
   *
   * @param bookingParameters
   * @param forceReload
   */
  public updateBookingParameters(bookingParameters: BookingParametersInterface, forceReload?: boolean) {
    // TODO: add sanity-checks here, before we cache the booking-params
    // if there are no shops yet loaded or the booking params are still unset or there's a new town to load or you force refresh, reset shops
    if (
      this._shops.length < 1 ||
      this.bookingParameters === undefined ||
      bookingParameters.slug.localeCompare(this._lastSearchedSlug) !== 0 ||
      forceReload
    ) {
      if (forceReload) {
        this.shops$ = null;
        this.offers$ = this.multicastObservableFactory.create(this.loadOfferData);
        this.outOfTownOffers$ = this.multicastObservableFactory.create(this.loadOutOfTownOfferData);
      }
      this.cacheBookingParameters(bookingParameters);
      // reset shops and offer cache
      this._shops = [];
      this._shopApiCache = null;
      this.offers = null;
      this.outOfTownOffers = null;
      this.updateShops();
      // shops are the same but offer needs to be updated (changes in people, ages or timeframe)
    } else {
      this.cacheBookingParameters(bookingParameters);
      this.outOfTownOffers = null;
      this.offers = null;
      // update offers and other related infos
      this.updateOffers();
    }
  }

  /**
   * function for factory to load shops
   */
  private loadShopData = (observer?) => {
    if (this._shopApiCache) {
      observer.next(this._shopApiCache);
      observer.complete();
    } else if (this.bookingParameters.slug.length) {
      this.apiService.getLocationsBySlug(this.bookingParameters).subscribe({
        next: data => {
          this._shopApiCache = data;
          observer.next(this._shopApiCache);
          observer.complete();
        },
        error: err => {
          console.error(`shop service can't fetch data from backend`, err);
          observer.next([]);
          observer.complete();
        }
      });
    } else {
      observer.next([]);
      observer.complete();
    }
  };

  /**
   * factory function to load shop offers
   */
  private loadOfferData = (observer?) => {
    if (this.offers?.length) {
      observer.next(this.offers);
      observer.complete();
    } else {
      if (
        this.bookingParameters.start &&
        this.bookingParameters.end &&
        this.bookingParameters.ages &&
        this.inTownShopIds.length > 0
      ) {
        if (this.customerCountryCode) {
          this.offerApiCall(observer);
        } else {
          // no country code yet, load from cart
          this.offerSub = this.platformService.customerCountryCode$.subscribe({
            next: customerCountryCode => {
              this.customerCountryCode = customerCountryCode;
              this.offerApiCall(observer);
            },
            complete: () => {
              this.offerSub?.unsubscribe();
            }
          });
        }
      } else {
        observer.next([]);
        observer.complete();
        // seems like shopIds didn't finish loading, try again?
      }
    }
  };

  /**
   * factory function to load shop offers
   */
  private loadOutOfTownOfferData = (observer?) => {
    if (this.outOfTownOffers?.length) {
      observer.next(this.outOfTownOffers);
      observer.complete();
    } else {
      if (
        this.bookingParameters.start &&
        this.bookingParameters.end &&
        this.bookingParameters.ages &&
        this.outOfTownShopIds.length > 0
      ) {
        if (this.customerCountryCode) {
          this.outOfTownOfferApiCall(observer);
        } else {
          this.platformCustomerCountrySub = this.platformService.customerCountryCode$.subscribe({
            next: customerCountryCode => {
              this.customerCountryCode = customerCountryCode;
              this.outOfTownOfferApiCall(observer);
            },
            complete: () => {
              this.platformCustomerCountrySub?.unsubscribe();
            }
          });
        }
      } else {
        observer.next([]);
        observer.complete();
        // seems like shopIds didn't finish loading, try again?
      }
    }
  };

  private outOfTownOfferApiCall(observer: Observer<OfferInterface[]>) {
    this.apiService
      .getOffers(this.bookingParameters.copy(), 'EUR', this.outOfTownShopIds, this.customerCountryCode)
      .subscribe({
        next: data => {
          this.outOfTownOffers = data;
          observer.next(data);
          observer.complete();
        },
        error: () => {
          this._shopApiCache = null; // reset shops to load fresh
          this.outOfTownOffers = null;
          this.loadingError.emit({
            bookingParameters: this.bookingParameters.copy(),
            error: 'loadOutOfTownOfferDataError'
          });
          observer.complete();
        }
      });
  }

  private offerApiCall(observer: Observer<OfferInterface[]>) {
    this.apiService
      .getOffers(this.bookingParameters.copy(), 'EUR', this.inTownShopIds, this.customerCountryCode)
      .subscribe({
        next: data => {
          this.offers = data;
          observer.next(data);
          observer.complete();
        },
        error: () => {
          this._shopApiCache = null; // reset shops to load fresh
          this.offers = null;
          this.loadingError.emit({
            bookingParameters: this.bookingParameters.copy(),
            error: 'loadOfferDataError'
          });
          observer.complete();
        }
      });
  }

  /**
   * Updates the shops
   *
   * @emits onShopUpdate
   * @param onlyShops
   */
  public updateShops(onlyShops = false) {
    if (!this.shops$) {
      this.shops$ = this.multicastObservableFactory.create<Array<ShopListInterface>>(this.loadShopData);
    }
    if (!onlyShops) {
      if (!this.offers$) {
        this.offers$ = this.multicastObservableFactory.create(this.loadOfferData);
      }
      if (!this.outOfTownOffers$) {
        this.outOfTownOffers$ = this.multicastObservableFactory.create(this.loadOutOfTownOfferData);
      }
    }
    this.updates.emit({
      type: eventTimeType.preShopUpdate,
      data: { bookingParameters: this.bookingParameters.copy() }
    });
    // this.preShopUpdate.emit(this.bookingParameters.copy());
    this.shops$.subscribe(parameters => {
      const shops = [];
      for (const shop of parameters) {
        shops.push(new Shop(shop));
      }
      // set shops to freshly loaded shops with the discountticker info
      // emit new shops in event
      this._shops = shops;
      this.updates.emit({
        type: eventTimeType.onShopUpdate,
        data: { bookingParameters: this.bookingParameters.copy(), shops: this._shops }
      });

      if (
        this._shops.length &&
        !onlyShops &&
        (this.isBrowser || this.bookingParameters.poi?.type !== 'AlpinShop')
      ) {
        // update offer info for townpage requests or if on client
        this.updateOffers();
      }
    });
  }

  private updateOffers() {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    if (
      this.bookingParameters.start &&
      this.bookingParameters.end &&
      this.bookingParameters.start.getTime() >= now.getTime() &&
      this.bookingParameters.end.getTime() >= this.bookingParameters.start.getTime()
    ) {
      if (this._shops.length > 0) {
        this.inTownOffer$ = new Subject();
        this.outOfTownOffer$ = new Subject();
        forkJoin([this.inTownOffer$, this.outOfTownOffer$]).subscribe(() => {
          this.updates.emit({
            type: eventTimeType.onAllTown,
            data: {
              bookingParameters: this.bookingParameters.copy(),
              shops: this._shops
            }
          });
        });
      }
      if (this._shops.length > 0) {
        this.updates.emit({
          type: eventTimeType.preInTown,
          data: { bookingParameters: this.bookingParameters.copy() }
        });
        this.offers$.subscribe({
          next: offers => {
            this.mapOffersToShops(offers);
            this.updates.emit({
              type: eventTimeType.onInTown,
              data: { bookingParameters: this.bookingParameters.copy(), shops: this._shops }
            });
            this.inTownOffer$.next({ bookingParameters: this.bookingParameters.copy(), shops: this._shops });
            this.inTownOffer$.complete();
          },
          error: error => {
            console.error(error);
          }
        });
        // TODO: might not be necessary to (pre)load on server. Could increase initial response time on large towns
        this.outOfTownOffers$.subscribe({
          next: offers => {
            this.mapOffersToShops(offers);
            this.updates.emit({
              type: eventTimeType.onOutOfTown,
              data: {
                bookingParameters: this.bookingParameters.copy(),
                shops: this._shops
              }
            });
            this.outOfTownOffer$.next({
              bookingParameters: this.bookingParameters.copy(),
              shops: this._shops
            });
            this.outOfTownOffer$.complete();
          },
          error: error => {
            console.error(error);
            // well, this should'nt happen. Invalid form? try loading again!
          }
        });
      } else {
        this.updates.emit({
          type: eventTimeType.onInTown,
          data: { bookingParameters: this.bookingParameters.copy(), shops: this._shops }
        });
        this.updates.emit({
          type: eventTimeType.onOutOfTown,
          data: { bookingParameters: this.bookingParameters.copy(), shops: this._shops }
        });
      }
    }
  }

  /**
   * this is where we map loaded offers to the given shop. The id to string casting
   * was necessary due to api updates that suddenly flipped between numbers and strings
   * @param offers
   */
  private mapOffersToShops(offers: Array<OfferInterface>) {
    for (const shop of this._shops) {
      const offer = offers.find(predicate => predicate.shopId.toString() === shop.id.toString());
      if (offer !== undefined) {
        shop.updateOffer(<OfferInterface>offer);
        shop.hasDiscount = true;
      }
    }
  }

  /**
   * writes the given bookingParameters to local and the session storage
   * @param bookingParameters bookingParameters you want to copy
   */
  cacheBookingParameters(bookingParameters: BookingParametersInterface) {
    this._lastSearchedSlug = bookingParameters.slug;
    this.bookingParameterService.saveBookingParameters(bookingParameters);
  }

  /**
   * @param slug the shopslug
   */
  getShopBySlug(slug: string, townslug: string): Shop | null {
    const shop = this.currentShops.find(
      _shop =>
        _shop.shopSlug === slug &&
        Object.values(_shop.townSlugs).includes(townslug.split('/').splice(0, 3).join('/'))
    );
    return shop;
  }

  /**
   * Returns the shop by the given od
   * @param id
   */
  getShopById(id: number): Shop | null {
    const shop = this.currentShops.find(_shop => +_shop.id === id);
    return shop ? shop : null;
  }
}
